import {
    HttpBackend,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import { AppGlobals } from '@app/app.global';
import { PmDemoModeService } from '@modules/purchase-manager/services/pm-demo-mode.service';

const DEMO_ASSET_BASE = '/assets/demo/purchase-manager';

/**
 * Serves Purchase Manager APIs from local JSON while the public demo route is active.
 * Replace files under assets/demo/purchase-manager without changing this mapping.
 */
@Injectable()
export class PmDemoInterceptor implements HttpInterceptor {
    constructor(
        private demoMode: PmDemoModeService,
        private httpBackend: HttpBackend,
        private appGlobals: AppGlobals
    ) { }

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (!this.demoMode.isActive || request.url.includes(DEMO_ASSET_BASE)) {
            return next.handle(request);
        }

        if (request.url.includes('sentry.io') || request.url.includes('google.com') || request.url.includes('recaptcha')) {
            return next.handle(request);
        }

        // Connect Now cities + lead submit must hit the real marketplaceadmin API.
        if (request.url.includes('marketplaceadmin')) {
            return next.handle(request);
        }

        if (!this.isAppApi(request.url)) {
            return next.handle(request);
        }

        const url = request.url;
        const method = request.method.toUpperCase();
        const body = request.body as any;

        if (url.includes('purchase-manager/analytics')) {
            return this.jsonResponse(method === 'GET' ? [] : { success: true });
        }

        if (url.includes('purchase-manager/status')) {
            const requestId = request.params.get('request_id') || request.params.get('requestId') || '';
            const itemName = this.demoMode.getCompareItemName(requestId) || 'Milk';
            return this.withSearchProductImage(itemName, (image) =>
                this.serveFile('purchase-manager-status.json', (data) => this.replaceItemName(data, itemName, image))
            );
        }

        if (url.includes('/purchase-manager') && method === 'POST' && !url.includes('analytics')) {
            const itemName = body?.item_name || body?.itemName || '';
            const requestId = this.demoMode.registerCompareRequest(itemName);
            return this.jsonResponse({ request_id: requestId });
        }

        if (url.includes('check-integration-status')) {
            return this.serveFile('check-integration-status.json');
        }

        if (url.includes('pm/fast-tags/recent-searched-items')) {
            return this.serveFile('recent-searched-items.json');
        }

        if (url.includes('pm/fast-tags/trending-near-you-items')) {
            return this.serveFile('trending-near-you-items.json');
        }

        if (url.includes('pm/fast-tags/hp-recent-purchases')) {
            return this.serveFile('hp-recent-purchases.json');
        }

        if (url.includes('most-searched-items') || url.includes('most-purchased-items') || url.includes('top-ordered-items')) {
            return this.serveFile('most-searched-items.json');
        }

        if (url.includes('last-20-purchase-items') || url.includes('last-searched-items')) {
            return this.serveFile('recent-searched-items.json');
        }

        if (url.includes('search-products')) {
            const query = (body?.search_term || body?.searchTerm || '').toString().toLowerCase();
            return this.serveFile('search-products.json', (items) => this.filterSearchResults(items, query));
        }

        if (url.includes('supplier-list')) {
            return this.serveFile('supplier-list.json');
        }

        if (url.includes('pm/favourites') || url.includes('favorite-items') || url.includes('favourite')) {
            return this.serveFile('favourites.json');
        }

        if (url.includes('pending-inventory-purchase-requests')) {
            return this.serveFile('inventory-purchase-requests.json');
        }

        if (url.includes('/cart') || url.includes('cart-summary') || url.includes('deliverit/cart')) {
            return this.jsonResponse({ response: { cart_products: [], cart_count: 0 } });
        }

        return this.jsonResponse(method === 'GET' ? [] : { success: true });
    }

    private isAppApi(url: string): boolean {
        const apis = this.appGlobals.apiUrl as Record<string, string>;
        if (Object.keys(apis).some(key => typeof apis[key] === 'string' && !!apis[key] && url.startsWith(apis[key]))) {
            return true;
        }
        return url.includes('/api/web/');
    }

    private filterSearchResults(items: any, query: string): any {
        const list = Array.isArray(items) ? items : [];
        if (!query) {
            return list.slice(0, 8);
        }
        return list.filter((item: any) => {
            const name = (item.item_name || item.itemName || item.name || '').toString().toLowerCase();
            return name.includes(query);
        });
    }

    private replaceItemName(data: any, itemName: string, image = ''): any {
        const serialized = JSON.stringify(data)
            .replace(/__ITEM_NAME__/g, itemName || 'Milk')
            .replace(/__PRODUCT_IMAGE__/g, image || '');
        return JSON.parse(serialized);
    }

    private withSearchProductImage(
        itemName: string,
        serveStatus: (image: string) => Observable<HttpEvent<unknown>>
    ): Observable<HttpEvent<unknown>> {
        const assetRequest = new HttpRequest('GET', `${DEMO_ASSET_BASE}/search-products.json`);
        return this.httpBackend.handle(assetRequest).pipe(
            filter((event: HttpEvent<any>): event is HttpResponse<any> => event instanceof HttpResponse),
            switchMap((event) => serveStatus(this.findSearchProductImage(event.body, itemName))),
            catchError(() => serveStatus(''))
        );
    }

    private findSearchProductImage(products: any, itemName: string): string {
        const list = Array.isArray(products) ? products : [];
        const wanted = (itemName || '').trim().toLowerCase();
        if (!wanted) {
            return list[0]?.product_image || '';
        }
        const exact = list.find((item: any) => String(item.item_name || '').toLowerCase() === wanted);
        if (exact?.product_image) {
            return exact.product_image;
        }
        const partial = list.find((item: any) => {
            const name = String(item.item_name || '').toLowerCase();
            return name.includes(wanted) || wanted.includes(name);
        });
        return partial?.product_image || list[0]?.product_image || '';
    }

    private serveFile(fileName: string, transform?: (body: any) => any): Observable<HttpEvent<unknown>> {
        const assetRequest = new HttpRequest('GET', `${DEMO_ASSET_BASE}/${fileName}`);
        return this.httpBackend.handle(assetRequest).pipe(
            map((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse) {
                    const body = transform ? transform(event.body) : event.body;
                    return event.clone({ body, status: 200 });
                }
                return event;
            }),
            catchError(() => this.jsonResponse([]))
        );
    }

    private jsonResponse(body: any): Observable<HttpEvent<unknown>> {
        return of(new HttpResponse({ status: 200, body }));
    }
}
